// ListWorkbench — a reusable filter/search/sort toolbar + Load-more footer for
// the long, ever-growing lists on Work Review, My Work, Work Assignments and My
// Assignments. Presentational only: the page owns query state (search, status,
// sort, offset) and data fetching. Design tokens throughout so it matches the
// app in light + dark.
import React from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/features/shared/components/ui/input';
import { Button } from '@/features/shared/components/ui/button';

/** Debounce a fast-changing value (e.g. a search box) before it drives fetches. */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export interface FilterChip {
  key: string;
  label: string;
  /** Optional live count rendered as "(n)". */
  count?: number;
}
export interface SortOpt {
  key: string;
  label: string;
}

/** Sum a status-count map over a chip key that may list several statuses ("a,b"). */
export function sumStatusCounts(counts: Record<string, number> | undefined, key: string): number {
  if (!counts) return 0;
  return key.split(',').reduce((n, s) => n + (counts[s.trim()] || 0), 0);
}
/** Grand total across every status in a count map (for the "All" chip). */
export const totalStatusCount = (counts: Record<string, number> | undefined): number =>
  counts ? Object.values(counts).reduce((a, b) => a + b, 0) : 0;

interface WorkbenchProps {
  search: string;
  onSearch: (v: string) => void;
  searchPlaceholder?: string;
  /** Status options WITHOUT "All" — the toolbar prepends an All chip. */
  statuses: FilterChip[];
  activeStatus: string; // '' = All
  onStatus: (key: string) => void;
  allLabel?: string;
  /** Optional count rendered on the "All" chip. */
  allCount?: number;
  sorts?: SortOpt[];
  activeSort?: string;
  onSort?: (key: string) => void;
  /** Optional right-aligned slot (e.g. a "More filters" toggle). */
  right?: React.ReactNode;
}

const chipCls = (active: boolean) =>
  `px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
    active
      ? 'bg-primary-500 text-white border-primary-500'
      : 'bg-background text-muted-foreground border-border hover:bg-muted'
  }`;

export const ListWorkbench: React.FC<WorkbenchProps> = ({
  search, onSearch, searchPlaceholder, statuses, activeStatus, onStatus,
  allLabel = 'All', allCount, sorts, activeSort, onSort, right,
}) => (
  <div className="space-y-3">
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
      <div className="relative flex-1 min-w-0">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={searchPlaceholder || 'Search…'}
          className="pl-8"
          aria-label={searchPlaceholder || 'Search'}
        />
      </div>
      {sorts && sorts.length > 0 && onSort && (
        <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5 shrink-0">
          {sorts.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => onSort(s.key)}
              className={`px-2.5 py-1 rounded text-xs whitespace-nowrap transition-colors ${
                activeSort === s.key ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
      {right}
    </div>
    <div className="flex flex-wrap gap-1.5">
      <button type="button" className={chipCls(activeStatus === '')} onClick={() => onStatus('')}>
        {allLabel}
        {typeof allCount === 'number' ? <span className="opacity-70"> ({allCount})</span> : null}
      </button>
      {statuses.map((s) => (
        <button key={s.key} type="button" className={chipCls(activeStatus === s.key)} onClick={() => onStatus(s.key)}>
          {s.label}
          {typeof s.count === 'number' ? <span className="opacity-70"> ({s.count})</span> : null}
        </button>
      ))}
    </div>
  </div>
);

interface LoadMoreProps {
  shown: number;
  total?: number;
  /** Explicit override — when total is unknown (e.g. assignments), pass whether the last page was full. */
  hasMore?: boolean;
  loading?: boolean;
  onMore: () => void;
  moreLabel?: string;
  countLabel?: (shown: number, total?: number) => string;
}

export const LoadMore: React.FC<LoadMoreProps> = ({
  shown, total, hasMore, loading, onMore, moreLabel = 'Load more', countLabel,
}) => {
  const showBtn = hasMore ?? (typeof total === 'number' ? shown < total : false);
  const text = countLabel
    ? countLabel(shown, total)
    : typeof total === 'number'
      ? `Showing ${shown} of ${total}`
      : `${shown} shown`;
  if (shown === 0 && !showBtn) return null;
  return (
    <div className="flex items-center justify-between pt-3 text-xs text-muted-foreground">
      <span>{text}</span>
      {showBtn && (
        <Button size="sm" variant="outline" onClick={onMore} disabled={loading}>
          {loading ? 'Loading…' : moreLabel}
        </Button>
      )}
    </div>
  );
};
